import { createIcpAgent, normalizeIcpCanisterId } from "./icAgent.js";
import { BAZAAR_SHARED_LEDGER } from "./bazaarRegistry.js";
import { BIONIQ_SEED_CANISTER_IDS } from "./bioniqSeedCanisters.js";
import { fetchExtPortfolioForCanister } from "./extPortfolio.js";
import { resolveIcpNftImageUrl } from "./icpImageResolver.js";
/**
 * ICRC-7 `tokens_of` page size (request chunk — not a holdings cap).
 * Bazaar shared ledger uses *offset* pagination; many other ledgers use *prev token id*.
 */
const PAGE_SIZE = 100;
/** Soft safety ceiling per ledger probe (not a UI display cap). */
const DEFAULT_MAX_ICRC7_TOKENS = 50_000;
/** Ledgers whose second arg is a list offset (skip N), not ICRC-7 prev-token cursor. */
function usesOffsetPagination(canisterId) {
    const id = normalizeIcpCanisterId(canisterId);
    return id === normalizeIcpCanisterId(BAZAAR_SHARED_LEDGER);
}
const MAX_BIONIQ_NFTS = 10_000;
const COLLECTION_CONCURRENCY = 6;
/** Bonsai Bazaar collection_assets — `/c/{collectionId}/{serial}/image`. */
export const BAZAAR_COLLECTION_ASSETS_CANISTER = "n42s3-syaaa-aaaau-agwqa-cai";
function extractString(value) {
    if ("Text" in value)
        return value.Text;
    if ("Nat" in value)
        return value.Nat.toString();
    if ("Int" in value)
        return value.Int.toString();
    return null;
}
function extractNat(value) {
    if ("Nat" in value)
        return value.Nat;
    if ("Int" in value)
        return value.Int >= 0n ? value.Int : null;
    if ("Text" in value && /^\d+$/.test(value.Text.trim())) {
        try {
            return BigInt(value.Text.trim());
        }
        catch {
            return null;
        }
    }
    return null;
}
function findInMap(map, ...keys) {
    for (const key of keys) {
        const entry = map.find(([k]) => k === key);
        if (entry)
            return entry[1];
    }
    return undefined;
}
function isHttpImageUrl(url) {
    if (!url?.trim())
        return false;
    return /^https?:\/\//i.test(url.trim());
}
function isDataImageUrl(url) {
    return Boolean(url?.trim().toLowerCase().startsWith("data:image/"));
}
function unwrapOptNat(v) {
    if (v == null)
        return null;
    if (typeof v === "bigint")
        return v;
    if (typeof v === "number" && Number.isFinite(v))
        return BigInt(Math.trunc(v));
    if (Array.isArray(v)) {
        if (v.length === 0)
            return null;
        return unwrapOptNat(v[0]);
    }
    return null;
}
function unwrapOptText(v) {
    if (v == null)
        return null;
    if (typeof v === "string")
        return v;
    if (Array.isArray(v)) {
        if (v.length === 0)
            return null;
        return unwrapOptText(v[0]);
    }
    return null;
}
function bazaarAssetImageUrl(collectionId, catalogSerial) {
    return `https://${BAZAAR_COLLECTION_ASSETS_CANISTER}.raw.icp0.io/c/${collectionId}/${catalogSerial}/image`;
}
/**
 * Parse ICRC-7 token metadata. Prefer HTTP asset URLs over huge data: embeds
 * (Bazaar ledger stores full PNGs in `image`, which blow query batch limits).
 */
export function extractIcrc7Metadata(metadata) {
    if (!metadata) {
        return {
            name: "Unknown NFT",
            imageUrl: null,
            collectionName: null,
            bazaarCollectionId: null,
            catalogSerial: null,
        };
    }
    const nameVal = findInMap(metadata, "icrc7:metadata:name", "name", "icrc7:name", "Name");
    const imageVal = findInMap(metadata, "icrc7:metadata:uri:image", "icrc7:image", "image", "thumbnail", "icrc7:metadata:thumbnail");
    const animVal = findInMap(metadata, "animation_url", "icrc7:metadata:uri:animation", "animation");
    const collectionNameVal = findInMap(metadata, "icrc7:collection_name", "collection_name", "collectionName");
    const collectionIdVal = findInMap(metadata, "icrc7:collection_id", "collection_id");
    const catalogSerialVal = findInMap(metadata, "trait:Catalog Serial", "Catalog Serial", "catalog_serial", "serial");
    const rawImage = imageVal ? resolveIcpNftImageUrl(extractString(imageVal)) : null;
    const rawAnim = animVal ? resolveIcpNftImageUrl(extractString(animVal)) : null;
    const bazaarCollectionId = collectionIdVal
        ? (extractNat(collectionIdVal)?.toString() ?? null)
        : null;
    const catalogSerial = catalogSerialVal
        ? extractString(catalogSerialVal)?.trim() || null
        : null;
    let imageUrl = null;
    let animationUrl = null;
    // Prefer direct HTTP / data from token metadata (ledger / asset canister files).
    // Bazaar collection_assets /c/{id}/{serial}/image is a fallback — marketplace
    // asset links break often; migrator/Canopy should load the NFT media file URL.
    // Keep image + animation separate so .mp4 animation_url is not dropped when a
    // still image also exists.
    const imageIsVideo = rawImage
        ? /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(rawImage) ||
            rawImage.toLowerCase().startsWith("data:video/")
        : false;
    const animIsVideo = rawAnim
        ? /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(rawAnim) ||
            rawAnim.toLowerCase().startsWith("data:video/")
        : false;
    if (isHttpImageUrl(rawImage) && !imageIsVideo) {
        imageUrl = rawImage;
    }
    else if (isDataImageUrl(rawImage)) {
        imageUrl = rawImage;
    }
    else if (isHttpImageUrl(rawImage) && imageIsVideo) {
        animationUrl = rawImage;
    }
    if (isHttpImageUrl(rawAnim) && animIsVideo) {
        animationUrl = rawAnim;
    }
    else if (isHttpImageUrl(rawAnim) && !imageUrl) {
        imageUrl = rawAnim;
    }
    else if (rawAnim?.toLowerCase().startsWith("data:video/") &&
        !animationUrl) {
        animationUrl = rawAnim;
    }
    if (!imageUrl && !animationUrl) {
        if (bazaarCollectionId && catalogSerial && /^\d+$/.test(catalogSerial)) {
            imageUrl = bazaarAssetImageUrl(bazaarCollectionId, catalogSerial);
        }
        else {
            imageUrl = rawImage ?? rawAnim;
        }
    }
    else if (!imageUrl &&
        bazaarCollectionId &&
        catalogSerial &&
        /^\d+$/.test(catalogSerial)) {
        // Video-only token — still attach collection_assets still as poster when possible
        imageUrl = bazaarAssetImageUrl(bazaarCollectionId, catalogSerial);
    }
    const descriptionVal = findInMap(metadata, "description", "icrc7:description", "icrc7:metadata:description");
    const utilityVal = findInMap(metadata, "utility", "utility_notes", "utilityNotes", "icrc7:utility", "trait:Utility", "trait:utility");
    const attributes = {};
    for (const [k, v] of metadata) {
        if (!/^trait:/i.test(k) && !/^attr:/i.test(k) && !/^property:/i.test(k)) {
            continue;
        }
        const label = k.replace(/^(trait|attr|property):/i, "").trim() || k;
        const text = extractString(v);
        if (text)
            attributes[label] = text;
        else {
            const n = extractNat(v);
            if (n != null)
                attributes[label] = n.toString();
        }
    }
    return {
        name: nameVal ? (extractString(nameVal) ?? "Unknown NFT") : "Unknown NFT",
        imageUrl,
        animationUrl,
        collectionName: collectionNameVal
            ? extractString(collectionNameVal)
            : null,
        bazaarCollectionId,
        catalogSerial,
        attributes: Object.keys(attributes).length ? attributes : undefined,
        description: descriptionVal ? extractString(descriptionVal) : null,
        utilityNotes: utilityVal ? extractString(utilityVal) : null,
    };
}
async function mapWithConcurrency(items, limit, fn) {
    const out = new Array(items.length);
    let idx = 0;
    async function worker() {
        while (idx < items.length) {
            const i = idx++;
            out[i] = await fn(items[i]);
        }
    }
    await Promise.all(Array.from({ length: Math.min(limit, Math.max(items.length, 1)) }, () => worker()));
    return out;
}
/**
 * Fetch ICRC-7 tokens owned by a principal on one collection canister.
 */
export async function fetchIcrc7Nfts(collectionCanisterId, ownerPrincipal, options = {}) {
    if (!collectionCanisterId || !ownerPrincipal)
        return [];
    const maxTokensRaw = options.maxTokens ?? DEFAULT_MAX_ICRC7_TOKENS;
    const maxTokens = Number.isFinite(maxTokensRaw) && maxTokensRaw > 0
        ? Math.floor(maxTokensRaw)
        : DEFAULT_MAX_ICRC7_TOKENS;
    const { Actor } = await import("@dfinity/agent");
    const { Principal } = await import("@dfinity/principal");
    const { IDL } = await import("@dfinity/candid");
    const agent = await createIcpAgent();
    const buildValueIdl = () => {
        const ValueRec = IDL.Rec();
        const Value = IDL.Variant({
            Nat: IDL.Nat,
            Int: IDL.Int,
            Text: IDL.Text,
            Blob: IDL.Vec(IDL.Nat8),
            Array: IDL.Vec(ValueRec),
            Map: IDL.Vec(IDL.Tuple(IDL.Text, ValueRec)),
        });
        ValueRec.fill(Value);
        return Value;
    };
    const idlFactory = () => {
        const Account = IDL.Record({
            owner: IDL.Principal,
            subaccount: IDL.Opt(IDL.Vec(IDL.Nat8)),
        });
        const Value = buildValueIdl();
        return IDL.Service({
            icrc7_tokens_of: IDL.Func([Account, IDL.Opt(IDL.Nat), IDL.Opt(IDL.Nat)], [IDL.Vec(IDL.Nat)], ["query"]),
            icrc7_token_metadata: IDL.Func([IDL.Vec(IDL.Nat)], [IDL.Vec(IDL.Opt(IDL.Vec(IDL.Tuple(IDL.Text, Value))))], ["query"]),
        });
    };
    const actor = Actor.createActor(idlFactory, {
        agent,
        canisterId: collectionCanisterId,
    });
    const ownerPpal = Principal.fromText(ownerPrincipal);
    const account = { owner: ownerPpal, subaccount: [] };
    /**
     * Paginate `icrc7_tokens_of`.
     * - Offset mode (Bazaar shared ledger): cursor = skip count.
     * - Prev mode (ICRC-7 draft): cursor = last token id from previous page.
     * If prev mode returns a full first page then an empty second page, fall back
     * to offset (Bazaar-style / non-standard ledgers).
     */
    async function paginateTokensOf(call, mode) {
        const out = [];
        const seen = new Set();
        let cursor = [];
        let activeMode = mode;
        let switchedToOffset = false;
        while (out.length < maxTokens) {
            const takeN = Math.min(PAGE_SIZE, maxTokens - out.length);
            const take = BigInt(takeN);
            let page;
            try {
                page = await call(cursor, [take]);
            }
            catch {
                break;
            }
            if (page.length === 0) {
                if (activeMode === "prev" && out.length >= PAGE_SIZE && !switchedToOffset) {
                    // First page was full under prev-cursor; empty next ⇒ try offset skip.
                    activeMode = "offset";
                    switchedToOffset = true;
                    cursor = [BigInt(out.length)];
                    continue;
                }
                break;
            }
            let added = 0;
            for (const id of page) {
                if (out.length >= maxTokens)
                    break;
                const k = id.toString();
                if (seen.has(k))
                    continue;
                seen.add(k);
                out.push(id);
                added += 1;
            }
            // Ledger ignored cursor and re-sent the same page — stop to avoid infinite dupes.
            if (added === 0)
                break;
            if (page.length < takeN)
                break;
            if (activeMode === "offset") {
                cursor = [BigInt(out.length)];
            }
            else {
                cursor = [page[page.length - 1]];
            }
        }
        return out;
    }
    const offsetMode = usesOffsetPagination(collectionCanisterId);
    let tokenIds = await paginateTokensOf((cursor, take) => actor.icrc7_tokens_of(account, cursor, take), offsetMode ? "offset" : "prev");
    // Some ledgers take Principal instead of Account.
    if (tokenIds.length === 0) {
        const principalActor = Actor.createActor(() => {
            const Value = buildValueIdl();
            return IDL.Service({
                icrc7_tokens_of: IDL.Func([IDL.Principal, IDL.Opt(IDL.Nat), IDL.Opt(IDL.Nat)], [IDL.Vec(IDL.Nat)], ["query"]),
                icrc7_token_metadata: IDL.Func([IDL.Vec(IDL.Nat)], [IDL.Vec(IDL.Opt(IDL.Vec(IDL.Tuple(IDL.Text, Value))))], ["query"]),
            });
        }, { agent, canisterId: collectionCanisterId });
        tokenIds = await paginateTokensOf((cursor, take) => principalActor.icrc7_tokens_of(ownerPpal, cursor, take), offsetMode ? "offset" : "prev");
    }
    if (tokenIds.length === 0)
        return [];
    const normalizeMetaRow = (row) => {
        if (!row || !Array.isArray(row) || row.length === 0)
            return null;
        return row;
    };
    /**
     * Bulk metadata often fails when ledgers embed multi-MB data: URLs.
     * Fetch one token at a time so Orbit Spots / Bazaar shared ledger works.
     */
    async function metadataForToken(id) {
        try {
            const rows = await actor.icrc7_token_metadata([id]);
            return normalizeMetaRow(rows[0]);
        }
        catch {
            return null;
        }
    }
    const isBazaarShared = normalizeIcpCanisterId(collectionCanisterId) ===
        normalizeIcpCanisterId(BAZAAR_SHARED_LEDGER);
    let lightActor = null;
    const collectionNameCache = new Map();
    function ensureLightActor() {
        if (lightActor)
            return lightActor;
        try {
            lightActor = Actor.createActor(() => IDL.Service({
                icrc7_collection_of: IDL.Func([IDL.Nat], [IDL.Opt(IDL.Nat)], ["query"]),
                tokenCatalogSerial: IDL.Func([IDL.Nat], [IDL.Opt(IDL.Nat)], ["query"]),
                collection_name: IDL.Func([IDL.Nat], [IDL.Opt(IDL.Text)], ["query"]),
            }), { agent, canisterId: collectionCanisterId });
            return lightActor;
        }
        catch {
            return null;
        }
    }
    async function resolveViaBazaarLight(tokenId) {
        const light = ensureLightActor();
        if (!light)
            return null;
        try {
            const [cidRaw, serialRaw] = await Promise.all([
                light.icrc7_collection_of(tokenId),
                light.tokenCatalogSerial(tokenId),
            ]);
            const bazaarCollectionId = unwrapOptNat(cidRaw);
            const catalogSerialNat = unwrapOptNat(serialRaw);
            if (bazaarCollectionId == null || catalogSerialNat == null)
                return null;
            const cidKey = bazaarCollectionId.toString();
            let collectionName;
            if (collectionNameCache.has(cidKey)) {
                collectionName = collectionNameCache.get(cidKey) ?? null;
            }
            else {
                try {
                    collectionName = unwrapOptText(await light.collection_name(bazaarCollectionId));
                }
                catch {
                    collectionName = null;
                }
                collectionNameCache.set(cidKey, collectionName);
            }
            const catalogSerial = catalogSerialNat.toString();
            return {
                name: collectionName
                    ? `${collectionName} #${catalogSerial}`
                    : `Token #${tokenId}`,
                imageUrl: bazaarAssetImageUrl(cidKey, catalogSerial),
                collectionName,
                bazaarCollectionId: cidKey,
                catalogSerial,
            };
        }
        catch {
            return null;
        }
    }
    const out = [];
    for (const tokenId of tokenIds) {
        let extracted = null;
        if (isBazaarShared) {
            const light = await resolveViaBazaarLight(tokenId);
            const meta = await metadataForToken(tokenId);
            if (meta) {
                extracted = extractIcrc7Metadata(meta);
                if (light) {
                    extracted = {
                        ...extracted,
                        collectionName: extracted.collectionName || light.collectionName,
                        bazaarCollectionId: extracted.bazaarCollectionId || light.bazaarCollectionId,
                        catalogSerial: extracted.catalogSerial || light.catalogSerial,
                        name: extracted.name === "Unknown NFT" ? light.name : extracted.name,
                    };
                }
                // Direct HTTP/data from metadata wins. Else fall back to collection_assets.
                if ((!extracted.imageUrl || isDataImageUrl(extracted.imageUrl)) &&
                    extracted.bazaarCollectionId &&
                    extracted.catalogSerial) {
                    // Keep data: only when no HTTP asset path; prefer Bazaar raw file over empty.
                    if (!isHttpImageUrl(extracted.imageUrl)) {
                        extracted = {
                            ...extracted,
                            imageUrl: bazaarAssetImageUrl(extracted.bazaarCollectionId, extracted.catalogSerial),
                        };
                    }
                }
            }
            else {
                extracted = light;
            }
        }
        if (!extracted) {
            const meta = await metadataForToken(tokenId);
            if (meta) {
                extracted = extractIcrc7Metadata(meta);
                // Prefer collection_assets HTTP when ledger still returned a data: blob.
                if ((!extracted.imageUrl || isDataImageUrl(extracted.imageUrl)) &&
                    extracted.bazaarCollectionId &&
                    extracted.catalogSerial) {
                    extracted = {
                        ...extracted,
                        imageUrl: bazaarAssetImageUrl(extracted.bazaarCollectionId, extracted.catalogSerial),
                    };
                }
            }
        }
        if (!extracted && !isBazaarShared) {
            extracted = await resolveViaBazaarLight(tokenId);
        }
        if (!extracted) {
            extracted = {
                name: `Token #${tokenId}`,
                imageUrl: null,
                collectionName: null,
                bazaarCollectionId: null,
                catalogSerial: null,
            };
        }
        const groupKey = extracted.bazaarCollectionId != null
            ? `${collectionCanisterId}#${extracted.bazaarCollectionId}`
            : collectionCanisterId;
        out.push({
            tokenId,
            name: extracted.name === "Unknown NFT"
                ? `Token #${tokenId}`
                : extracted.name,
            imageUrl: extracted.imageUrl,
            animationUrl: extracted.animationUrl ?? null,
            collectionId: collectionCanisterId,
            collectionName: extracted.collectionName,
            bazaarCollectionId: extracted.bazaarCollectionId,
            catalogSerial: extracted.catalogSerial,
            groupKey,
            attributes: extracted.attributes,
            description: extracted.description,
            utilityNotes: extracted.utilityNotes,
        });
    }
    return out;
}
export function curatedBioniqCanisters(watchCanisters = []) {
    const set = new Set();
    for (const id of BIONIQ_SEED_CANISTER_IDS) {
        const n = normalizeIcpCanisterId(id);
        if (n)
            set.add(n);
    }
    for (const id of watchCanisters) {
        const n = normalizeIcpCanisterId(id);
        if (n)
            set.add(n);
    }
    return [...set];
}
/**
 * Bioniq ICP collections — EXT ledgers (`sale_canister` on api.bioniq.io).
 * Owner must be the Bioniq wallet principal that holds the EXT tokens.
 * (Not ICRC-7 — verified via extensions(): @ext/common + @ext/nonfungible.)
 */
export async function fetchBioniqOrdinalsPortfolio(ownerPrincipal, watchCanisters = [], options) {
    const owner = ownerPrincipal.trim();
    if (owner.length < 27 || owner.length > 64)
        return [];
    const canisters = curatedBioniqCanisters(watchCanisters);
    if (canisters.length === 0)
        return [];
    const maxNfts = options?.maxNfts ?? MAX_BIONIQ_NFTS;
    const perCollection = await mapWithConcurrency(canisters, COLLECTION_CONCURRENCY, async (collectionId) => {
        try {
            const nfts = await fetchExtPortfolioForCanister(collectionId, owner, {
                maxTokens: options?.maxTokensPerCollection ?? 10_000,
            });
            return nfts.map((n) => ({
                ...n,
                chain: "ckbtc_ordinals",
                name: n.name.startsWith("Token #")
                    ? `Bioniq #${n.tokenId}`
                    : n.name,
            }));
        }
        catch {
            return [];
        }
    });
    const out = [];
    for (const list of perCollection) {
        for (const n of list) {
            out.push(n);
            if (out.length >= maxNfts)
                return out;
        }
    }
    return out;
}
